<template>
  <div>
    <div class="absolute-elements">
      <NavBar :sections="sections" @scroll-to-section="scrollToSection" />
    </div>
    <div class="fullpage-container" @scroll="handleScroll">
      <WaveAnimation :visible="showWave" />
      
      <!-- Hero Section -->
      <div class="section" id="slide1">
        <div id="slide1-container">
          <!-- Replace video element with BackgroundVideo component -->
          <BackgroundVideo
            desktopVideo="videos/TrailerLandingVideo.mp4"
            mobileVideo="videos/TrailerLandingVideoMobile.mp4"
            placeholderImage="videos/TrailerLandingVideo.jpg"
            altText="STE Trailers"
            videoId="trailerVideo"
            @video-loaded="handleVideoLoad"
            @video-error="handleVideoError"
          />
          
          <!-- Hero Text with Metallic Effect -->
          <div class="slideText">
            <div class="sub-text-with-image">
              <div class="text-content">
                <h3 class="slideSub text-5xl rounded-4xl bg-primary border-1 border-base-content text-base-content p-4">A Major Step Forward...</h3>
                <h4 class="ml-4 mt-6 text-xl text-secondary">in Stainless Tanker Manufacturing</h4>
                <div class="ml-4 mt-6 border-b-2 border-base-content w-1/3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Section Separator -->
      <SectionSeparator />
      
      <!-- Section with First Trailer -->
      <div class="section" id="slide2">
        <div class="section-content">
          <div class="section-header">
            <h2 class="section-title mt-10">Explore Our <span class="highlight">Premium Trailers</span></h2>
            <div class="section-subtitle">Engineered for Excellence</div>
          </div>
          <TrailerDiagram 
            title="3-A Sanitary Trailers"
            base-image="/img/trailers/3ASanitaryBase.png"
            fallback-image="/img/fallback-trailer.png"
            :ping-points="trailerData.sanitary.pingPoints"
            :table-data="trailerData.sanitary.tableData"
            :table-images="trailerData.sanitary.tableImages"
            :table-title-images="trailerData.sanitary.tableTitleImages"
          />
        </div>
      </div>
      
      <!-- Remaining Trailer Sections -->
      <div class="section" id="slide3">
        <div class="section-content">
          <TrailerDiagram 
            title="Food Grade Trailers"
            base-image="/img/trailers/FoodGradeBase.png"
            fallback-image="/img/fallback-trailer.png"
            :ping-points="trailerData.foodGrade.pingPoints"
            :table-data="trailerData.foodGrade.tableData"
            :table-images="trailerData.foodGrade.tableImages"
            :table-title-images="trailerData.foodGrade.tableTitleImages"
          />
        </div>
      </div>
      
      <div class="section" id="slide4">
        <div class="section-content">
          <TrailerDiagram 
            title="Chemical Trailers"
            base-image="/img/trailers/ChemicalBase.png"  
            fallback-image="/img/fallback-trailer.png"
            :ping-points="trailerData.chemical.pingPoints"
            :table-data="trailerData.chemical.tableData"
            :table-images="trailerData.chemical.tableImages"
            :table-title-images="trailerData.chemical.tableTitleImages"
          />
        </div>
      </div>
      
      <div class="section" id="slide5">
        <div class="section-content">
          <TrailerDiagram 
            title="ASME Trailers"
            base-image="/img/trailers/ASMEBase.png"  
            fallback-image="/img/fallback-trailer.png"
            :ping-points="trailerData.asme.pingPoints"
            :table-data="trailerData.asme.tableData"
            :table-images="trailerData.asme.tableImages"
            :table-title-images="trailerData.asme.tableTitleImages"
          />
        </div>
      </div>
      
      <div class="section" id="slide6">
        <div class="section-content">
          <TrailerDiagram 
            title="Double Conical Trailers"
            base-image="/img/trailers/FertilizerBase.png"  
            fallback-image="/img/fallback-trailer.png"
            :ping-points="trailerData.fertilizer.pingPoints"
            :table-data="trailerData.fertilizer.tableData"
            :table-images="trailerData.fertilizer.tableImages"
            :table-title-images="trailerData.fertilizer.tableTitleImages"
          />
        </div>
      </div>
      
      <!-- Add new section for Trailers Slider -->
      <div class="section" id="slide7">
        <div class="section-content">
          <TrailersSlider />
        </div>
      </div>
    </div>
    
    <!-- Scroll Navigation Arrows -->
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollUp"
        :icon="isUpHovered ? 'angles-up' : 'chevron-up'"
        class="scroll-arrow up-arrow"
        @click="scrollToPreviousSection"
        @mouseenter="isUpHovered = true"
        @mouseleave="isUpHovered = false"
      />
    </transition>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollDown"
        :icon="isDownHovered ? 'angles-down' : 'chevron-down'"
        class="scroll-arrow down-arrow"
        @click="scrollToNextSection"
        @mouseenter="isDownHovered = true"
        @mouseleave="isDownHovered = false"
      />
    </transition>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue';
import TrailerDiagram from '@/components/TrailerDiagram.vue';
import SectionSeparator from '@/components/SectionSeparator.vue';
import TrailersSlider from '@/components/TrailersSlider.vue';
import BackgroundVideo from '@/components/BackgroundVideo.vue';
import WaveAnimation from '@/components/WaveAnimation.vue';
import { nextTick } from 'vue';

export default {
  name: 'TrailerPage',
  components: {
    NavBar,
    TrailerDiagram,
    SectionSeparator,
    TrailersSlider,
    BackgroundVideo,
    WaveAnimation
  },
  data() {
    return {
      showScrollUp: false,
      showScrollDown: true,
      isUpHovered: false,
      isDownHovered: false,
      videoLoading: true,
      videoReady: false,
      showWave: false,
      sections: [
        { id: 'slide2', name: '3-A SANITARY', bgClass: 'bg-gray-200' },
        { id: 'slide3', name: 'FOOD GRADE', bgClass: 'bg-gray-200' },
        { id: 'slide4', name: 'DOT407 CHEMICAL', bgClass: 'bg-gray-300' },
        { id: 'slide5', name: 'ASME', bgClass: 'bg-gray-400' },
        { id: 'slide6', name: 'DOUBLE CONICAL', bgClass: 'bg-gray-400' },
        { id: 'slide7', name: 'GALLERY', bgClass: 'bg-gray-500' },
      ],
      trailerData: {
        sanitary: {
          pingPoints: [
            {
              top: '0%',
              left: '30%',
              title: 'Vessel',
              text: 'Inside: Stainless Steel two-piece barrel (304 or 316) finished in 2B, and outer wrap in a #4 or BA finish.'
            },
            {
              top: '75%',
              left: '45%',
              title: 'Suspensions',
              text: 'Ridewell or Hendrickson suspension packages standard, as specified. Two, three, four or more axles with optional lift kits.'
            },
            {
              top: '60%',
              left: '15%',
              title: 'Rear Head',
              text: 'Transport, Direct Ship (housing) or Farm Pickup (cabinet) options available.'
            },
            {
              top: '25%',
              left: '95%',
              title: 'Front Head',
              text: '7-way receptacle on stainless bracket located below centerline of front head'
            },
            {
              top: '30%',
              left: '50%',
              title: 'Insulation',
              text: '6" compressed to 5" fiberglass or 5" polyiso with sprayed heads'
            },
            {
              top: '0%',
              left: '70%',
              title: 'Top Access',
              text: 'Curbside and/or streetside ladder with optional platform/walkway configurations'
            },
            {
              top: '80%',
              left: '20%',
              title: 'Underride Guard',
              text: 'DOT Compliant rear underride protection standard'
            },
            {
              top: '60%',
              left: '2%',
              title: 'Lighting and Safety',
              text: 'Peterson or Truck Lite light harnesses standard, as specified. Optional additional lights including ring-of-fire. <br /> Conspicuity tape per DOT regulations.'
            },
            {
              top: '40%',
              left: '60%',
              title: 'GPS / Temperature System',
              text: 'Customer specified and supplied system'
            }
          ],
          tableData: {
            capacity: '6,200 gal. <br />7,500 gal.',
            features: [
              'Straight Round',
              'Insulated Tank',
              'Custom designs may be available in larger quantities'
            ],
            description: 'Interact with the diagram to receive more detailed information on each part.'
          },
          tableTitleImages: [
            { title: 'Transport', image: '/img/trailers/3ASanitaryList1.png' },
            { title: 'Direct Ship', image: '/img/trailers/3ASanitaryList2.png' }
          ]
        },
        foodGrade: {
          pingPoints: [
            {
              top: '0%',
              left: '30%',
              title: 'Vessel',
              text: 'Inside: Stainless Steel two-piece barrel (304 or 316) finished in 2B, and outer wrap in a #4 or BA finish.'
            },
            {
              top: '75%',
              left: '45%',
              title: 'Suspensions',
              text: 'Ridewell or Hendrickson suspension packages standard, as specified. Two, three, four or more axles with optional lift kits.'
            },
            {
              top: '60%',
              left: '15%',
              title: 'Rear Head',
              text: 'Cabinet with optional equipment, as specified. Optional equipment to include pumps, valves.'
            },
            {
              top: '25%',
              left: '95%',
              title: 'Front Head',
              text: '7-way receptacle on stainless bracket located below centerline of front head'
            },
            {
              top: '30%',
              left: '50%',
              title: 'Insulation',
              text: '6" compressed to 5" fiberglass or 5" polyiso with sprayed heads'
            },
            {
              top: '27%',
              left: '80%',
              title: 'Heat',
              text: 'Full length (3) channel heat section with fittings, relief valve, with steam & intransit heat connections at front head and steam trap at rear. 150 PSI design working pressure'
            },
            {
              top: '0%',
              left: '70%',
              title: 'Top Access',
              text: 'Curbside and/or streetside ladder with optional platform/walkway configurations'
            },
            {
              top: '0%',
              left: '60%',
              title: 'Spilldam',
              text: 'Stainless steel fully welded floor spill dam <br /> (2) 1 ½" diameter drain hoses located on each side at rear of spill dam'
            },
            {
              top: '80%',
              left: '20%',
              title: 'Underride Guard',
              text: 'DOT Compliant rear underride protection standard'
            },
            {
              top: '60%',
              left: '2%',
              title: 'Lighting and Safety',
              text: 'Peterson or Truck Lite light harnesses standard, as specified. Optional additional lights including ring-of-fire. <br /> Conspicuity tape per DOT regulations.'
            },
            {
              top: '40%',
              left: '60%',
              title: 'GPS / Temperature System',
              text: 'Customer specified and supplied system'
            }
          ],
          tableData: {
            title: 'Food Grade',
            capacity: '6,800 gal. <br />7,000 gal.',
            features: [
              'Straight Round',
              'Insulated Tank',
              'Custom designs may be available in larger quantities'
            ],
            description: 'Interact with the diagram to receive more detailed information on each part.'
          },
          tableTitleImages: [
            { title: 'Standard', image: '/img/trailers/FoodGradeList.png' }
          ]
        },
        chemical: {
          pingPoints: [
            // Sample data - replace with actual ping points for chemical trailers
            {
              top: '0%',
              left: '30%',
              title: 'Vessel',
              text: 'Inside: Stainless Steel two-piece barrel (304, 316 or 2025) finished in 2B, and outer wrap in a #4 or BA finish.'
            },
            {
              top: '75%',
              left: '45%',
              title: 'Suspensions',
              text: 'Ridewell or Hendrickson suspension packages standard, as specified.  Two, three, four or more axles with optional lift kits.'
            },
            {
              top: '60%',
              left: '15%',
              title: 'Rear Head',
              text: 'Cabinet with optional equipment, as specified.  Optional equipment to include pumps, valves.'
            },
            {
              top: '25%',
              left: '95%',
              title: 'Front Head',
              text: '7-way receptacle on stainless bracket located below centerline of front head'
            },
            {
              top: '30%',
              left: '50%',
              title: 'Insulation',
              text: '6" compressed to 5" fiberglass or 5" polyiso with sprayed heads'
            },
            {
              top: '27%',
              left: '80%',
              title: 'Heat',
              text: 'Full length (3) channel heat section with fittings, relief valve, with steam & intransit heat connections at front head and steam trap at rear. 150 PSI design working pressure'
            },
            {
              top: '0%',
              left: '70%',
              title: 'Top Access',
              text: 'Curbside and/or streetside ladder with optional platform/walkway configurations'
            },
            {
              top: '0%',
              left: '60%',
              title: 'Spilldam',
              text: 'Stainless steel fully welded floor spill dam <br /> (2) 1 ½” diameter drain hoses located on each side at rear of spill dam'
            },
            {
              top: '80%',
              left: '20%',
              title: 'Underride Guard',
              text: 'DOT Compliant rear underride protection standard'
            },
            {
              top: '60%',
              left: '2%',
              title: 'Lighting and Safety',
              text: 'Peterson or Truck Lite light harnesses standard, as specified. Optional additional lights including ring-of-fire. <br /> Conspicuity tape per DOT regulations.'
            },
            {
              top: '40%',
              left: '60%',
              title: 'GPS / Temperature System',
              text: 'Customer specified and supplied system'
            }
          ],
          tableData: {
            title: 'Chemical',
            capacity: '5,000 gal. <br />7,000 gal.',
            features: [
              'Straight Round',
              'Un-Insulated Tank Optional',
              'Custom designs may be available in larger quantities'
            ],
            description: 'Interact with the diagram to receive more detailed information on each part.'
          },
          tableTitleImages: [
            { title: 'Chemical', image: '/img/trailers/ChemicalList.png' }
          ]
        },
        asme: {
          pingPoints: [
            // Sample data - replace with actual ping points for ASME trailers
            {
              top: '0%',
              left: '30%',
              title: 'Vessel',
              text: 'Inside: Stainless Steel two-piece barrel (304 or 316) finished in 2B or #4.'
            },
            {
              top: '75%',
              left: '45%',
              title: 'Suspensions',
              text: 'Ridewell or Hendrickson suspension packages standard, as specified.  Two, three, four or more axles with optional lift kits.'
            },
            {
              top: '60%',
              left: '15%',
              title: 'Rear Head',
              text: 'Cabinet with optional equipment, as specified.  Optional equipment to include pumps, valves.'
            },
            {
              top: '0%',
              left: '70%',
              title: 'Top Access',
              text: 'Curbside and/or streetside ladder with optional platform/walkway configurations'
            },
            {
              top: '80%',
              left: '20%',
              title: 'Underride Guard',
              text: 'DOT Compliant rear underride protection standard'
            },
            {
              top: '60%',
              left: '2%',
              title: 'Lighting and Safety',
              text: 'Peterson or Truck Lite light harnesses standard, as specified. Optional additional lights including ring-of-fire. <br /> Conspicuity tape per DOT regulations.'
            },
            {
              top: '40%',
              left: '60%',
              title: 'GPS / Temperature System',
              text: 'Customer specified and supplied system'
            }
          ],
          tableData: {
            title: 'ASME',
            capacity: '6,200 gal. <br />7,500 gal.',
            features: [
              'Straight Round',
              'Custom designs may be available in larger quantities'
            ],
            description: 'Interact with the diagram to receive more detailed information on each part.'
          },
          tableTitleImages: [
            { title: 'ASME', image: '/img/trailers/ASMEList.png' }
          ]
        },
        fertilizer: {
          pingPoints: [
            // Sample data - replace with actual ping points for double conical trailers
            {
              top: '0%',
              left: '30%',
              title: 'Vessel',
              text: 'Inside: Stainless Steel two-piece barrel (304 or 316) finished in 2B or #4.'
            },
            {
              top: '75%',
              left: '45%',
              title: 'Suspensions',
              text: 'Ridewell or Hendrickson suspension packages standard, as specified.  Two, three, four or more axles with optional lift kits.'
            },
            {
              top: '60%',
              left: '15%',
              title: 'Rear Head',
              text: 'Cabinet with optional equipment, as specified.  Optional equipment to include pumps, valves.'
            },
            {
              top: '0%',
              left: '70%',
              title: 'Top Access',
              text: 'Curbside and/or streetside ladder with optional platform/walkway configurations'
            },
            {
              top: '80%',
              left: '20%',
              title: 'Underride Guard',
              text: 'DOT Compliant rear underride protection standard'
            },
            {
              top: '60%',
              left: '2%',
              title: 'Lighting and Safety',
              text: 'Peterson or Truck Lite light harnesses standard, as specified. Optional additional lights including ring-of-fire. <br /> Conspicuity tape per DOT regulations.'
            },
            {
              top: '40%',
              left: '60%',
              title: 'GPS / Temperature System',
              text: 'Customer specified and supplied system'
            }
          ],
          tableData: {
            title: 'Double Conical',
            capacity: '5,600 gal. <br />6,000 gal. <br />6,500 gal. <br />7,000 gal.',
            features: [
              'Double Conical',
              'Insulated Tank Optional',
              'Custom designs may be available in larger quantities'
            ],
            description: 'Interact with the diagram to receive more detailed information on each part.'
          },
          tableTitleImages: [
            { title: 'Double Conical', image: '/img/trailers/FertilizerList.png' }
          ]
        }
      }
    };
  },
  mounted() {
    this.initIntersectionObserver();
    this.handleScroll();

    this.scrollToHash();

    // Watch for route changes to handle hash scrolling
    this.$watch('$route', (to) => {
      if (to.hash) {
        this.scrollToHash();
      }
    });
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    // ...existing code...
    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            console.log(`Section ${entry.target.id} is now visible`);
          } else {
            entry.target.classList.remove('visible');
            console.log(`Section ${entry.target.id} is now hidden`);
          }
        });
      }, options);

      document.querySelectorAll('.section').forEach((section) => {
        this.observer.observe(section);
      });
    },
    handleScroll() {
      const container = document.querySelector('.fullpage-container');
      this.showScrollUp = container.scrollTop > 0;
      this.showScrollDown =
        container.scrollTop + container.clientHeight < container.scrollHeight;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    },
    scrollToNextSection() {
      const container = document.querySelector('.fullpage-container');
      const sections = document.querySelectorAll('.section');
      const currentScroll = container.scrollTop + container.clientHeight / 2;
      let nextSection = null;

      sections.forEach((section) => {
        if (section.offsetTop > currentScroll && !nextSection) {
          nextSection = section;
        }
      });

      if (nextSection) {
        console.log(`Scrolling to next section: ${nextSection.id}`);
        container.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
      } else {
        console.log('No next section found');
      }
    },
    scrollToPreviousSection() {
      const container = document.querySelector('.fullpage-container');
      const sections = document.querySelectorAll('.section');
      const currentScroll = container.scrollTop + container.clientHeight / 2;
      let previousSection = null;

      sections.forEach((section, index) => {
        if (section.offsetTop < currentScroll) {
          previousSection = sections[index - 1];
        }
      });

      if (previousSection) {
        console.log(`Scrolling to previous section: ${previousSection.id}`);
        container.scrollTo({ top: previousSection.offsetTop, behavior: 'smooth' });
      } else {
        console.log('No previous section found');
      }
    },
    scrollToTop() {
      const container = document.querySelector('.fullpage-container');
      container.scrollTo({ top: 0, behavior: 'smooth' });
    },
    scrollToSection(id) {
      console.log(`Scrolling to section: ${id}`);
      const container = document.querySelector('.fullpage-container');
      const element = document.getElementById(id);
      if (element) {
        container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      }
    },
    scrollToHash() {
      nextTick(() => {
        setTimeout(() => { // Add a delay to ensure the element is fully rendered
          const hash = this.$route.hash;
          if (hash) {
            const element = document.querySelector(hash);
            if (element) {
              console.log(`Scrolling to element: ${hash}`);
              element.scrollIntoView({ behavior: 'smooth' });
            } else {
              console.log(`Element not found for hash: ${hash}`);
            }
          }
        }, 500); // Adjust delay as necessary
      });
    },
    handleVideoLoad() {
      this.videoLoading = false;
      this.videoReady = true;
      console.log('Trailer video loaded successfully');
    },
    handleVideoError() {
      console.error('Error loading trailer video');
      this.videoLoading = false;
    }
  },
};
</script>

<style scoped lang="scss">

.absolute-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  z-index: 100;
}

/* Main Container */
.fullpage-container {
  overflow-y: auto;
  height: 100vh;
  background: radial-gradient(ellipse at bottom, #363741 0%, #1d1e26 90%);
  background-attachment: fixed;
  position: relative;
  z-index: 0;
  scroll-behavior: smooth;
}

/* Hero Section */
#slide1-container {
  min-height: 100vh;
  z-index: -25;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: left;
  padding: 0% 5%;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden; /* Add this to contain absolutely positioned children */
}

#bgVideo {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
}

/* Section Styling */
.section {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
  padding: 2rem 0;
}

.section-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  max-width: 1450px;
  margin-top: 2rem;
  width: 100%;
  height: 100%;
}

/* Hero Text */
.slideText {
  max-width: 70%;
  margin-top: 70vh;
  margin-left: 2vw;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: rgb(183, 185, 186);
  position: relative;
  z-index: 10; /* Ensure text stays above video */

  @media (max-width: 768px) {
    margin-top: 58vh;
    margin-left: 1vw;
    max-width: 100%;
    text-align: left;
  }
}

.sub-text-with-image {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.text-content {
  display: flex;
  flex-direction: column;
}

/* Section Headers */
.section-header {
  width: 100%;
  max-width: 1200px;
  text-align: center;
  margin-bottom: 2rem;
  opacity: 0;
  transform: translateY(30px);
  animation: fadeInUp 0.8s forwards;
  animation-delay: 0.2s;
}

.section-title {
  font-size: 2.5rem;
  font-weight: bold;
  color: rgb(214, 214, 214);
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #b7b9ba 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
}

.section-title .highlight {
  background: linear-gradient(135deg, #ff0000 0%, #8f0000 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  position: relative;
}

.section-title .highlight::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, transparent, #8f0000, transparent);
}

.section-subtitle {
  font-size: 1.25rem;
  color: rgba(183, 185, 186, 0.8);
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
}

/* Scroll Navigation */
.scroll-arrow {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  font-size: 2.5rem;
  color: #b7b9ba;
  cursor: pointer;
  z-index: 100;
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.5));
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.3s ease,
    bottom 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.scroll-arrow:hover {
  color: #ffffff;
  transform: translateX(-50%) scale(1.2);
}

.up-arrow {
  /* Ride up by the inventory ticker's height while it's open (0 when closed) */
  bottom: calc(80px + var(--ticker-offset, 0px));
  animation: float-up 2s infinite;

  @media (max-width: 768px) {
    bottom: calc(60px + var(--ticker-offset, 0px));
  }
}

.down-arrow {
  bottom: calc(20px + var(--ticker-offset, 0px));
  animation: float-down 2s infinite;
}

@keyframes float-up {
  0%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(-10px);
  }
}

@keyframes float-down {
  0%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(10px);
  }
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .section {
    padding: 1.5rem 1rem;
  }
  
  .section-content {
    margin-top: 1rem;
  }
}

@media (max-width: 768px) {
  .section {
    padding: 1rem 0.5rem;
  }
  
  .up-arrow {
    display: none;
  }
}
</style>